import { Box, Container, Dialog, Paper, Typography } from "@mui/material";
import { FC, useEffect, useRef, useState } from "react";
import SettingsForm, { SettingsFormSubmitHandler } from "./SettingsForm";
import { Business, InsertPhoto, VpnKey } from "@mui/icons-material";
import InfoBoxGroup from "../base/InfoBoxGroup";
import InfoBox from "../base/InfoBox";
import { useDispatch, useSelector } from "../../store";
import CompanySettings from "../../types/companySettings";
import Button from "../base/Button";
import {
  deleteCompany,
  editCompany,
  fetchCompanyList,
} from "../../slices/company";
import { useNavigate } from "react-router-dom";
import DeleteConfirmation from "../base/DeleteConfirmation";
import checkRole, { UserRoles } from "../../helpers/checkUserRoles";
import { logout } from "../../slices/auth";

const Settings: FC = (props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { status, selectedCompany } = useSelector((store) => store.company);
  const { dbUser, status: authStatus } = useSelector((store) => store.auth);

  const [modalOpen, setModalOpen] = useState<"delete" | "auth" | "none">(
    "none"
  );

  const formSubmitHandlerRef = useRef<SettingsFormSubmitHandler | null>(null);

  const handleSave = async (data: CompanySettings) => {
    const newSso =
      data.useSso && data.ssoSettings && !selectedCompany?.settings?.useSso;

    const newCompany = {
      ...selectedCompany!,
      settings: data,
    };

    await dispatch(editCompany(newCompany!)).then((result) => {
      if (result && result.meta.requestStatus === "fulfilled" && newSso) {
        setModalOpen("auth");
      }
    });
  };

  const handleOpenDelete = () => {
    setModalOpen("delete");
  };

  const handleClose = () => {
    setModalOpen("none");
  };

  const handleDelete = async () => {
    await dispatch(deleteCompany(selectedCompany?.id!));
    handleClose();
    dispatch(fetchCompanyList());
    navigate("/select");
  };

  const handleLogout = () => {
    handleClose();
    dispatch(logout());
  };

  useEffect(() => {
    if (!selectedCompany?.finishedSetup) {
      navigate("/setup");
    }
  }, [selectedCompany?.finishedSetup, navigate]);

  useEffect(() => {
    if (!selectedCompany) {
      navigate("/select");
    }
  }, [selectedCompany, navigate]);

  const infoBoxes = [
    {
      text: "Update your company details to continue setting up your account.",
      icon: <Business fontSize="inherit" color="primary" />,
    },
    {
      text: "Upload a logo for downloads and emails. If you don’t upload one, the SVPRO logo will be used.",
      icon: <InsertPhoto fontSize="inherit" color="warning" />,
    },
    {
      text: "Configure access by choosing authentication options. If using SSO, provide configuration details.",
      icon: <VpnKey fontSize="inherit" color="secondary" />,
    },
  ];

  return (
    <Container>
      <InfoBoxGroup
        header=""
        content={infoBoxes.map((info, index) => {
          return <InfoBox key={index} text={info.text} icon={info.icon} />;
        })}
      />
      <Paper
        sx={{
          paddingY: 3,
          paddingX: { xs: 2, md: 5 },
          marginBottom: 5,
        }}
      >
        <SettingsForm onSave={handleSave} ref={formSubmitHandlerRef} />
        <Box
          display="flex"
          flexDirection="row"
          width="100%"
          justifyContent="space-between"
          gap={2}
          marginTop={2}
        >
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            visibility={
              checkRole(dbUser!, UserRoles.GlobalAdmin) ? "visible" : "hidden"
            }
          >
            <Button
              label="Delete"
              variant={"contained"}
              color={"error"}
              onClick={handleOpenDelete}
              disabled={status === "loading"}
            />
          </Box>
          <Box display="flex" flexDirection="column" justifyContent="center">
            <Button
              label="Save"
              variant={"contained"}
              color={"primary"}
              onClick={() => formSubmitHandlerRef.current?.()}
              disabled={status === "loading"}
            />
          </Box>
        </Box>
      </Paper>
      <Dialog open={modalOpen === "delete"}>
        <DeleteConfirmation
          name="this company"
          handleClose={handleClose}
          handleDelete={handleDelete}
          loading={status === "loading"}
        />
      </Dialog>
      <Dialog open={modalOpen === "auth"}>
        <Box margin={3}>
          <Typography variant="h4" justifyContent="center" textAlign="center">
            SSO settings saved. Please reauthenticate to complete setup.
          </Typography>
          <Box
            display="flex"
            flexDirection="row"
            width="100%"
            justifyContent="space-around"
            gap={2}
            marginTop={5}
          >
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              color="text.secondary"
            >
              <Button label="Cancel" cancel onClick={handleClose} />
            </Box>
            <Box display="flex" flexDirection="column" justifyContent="center">
              <Button
                label="Log Out"
                variant={"contained"}
                color={"secondary"}
                onClick={handleLogout}
                loading={authStatus === "loading"}
              />
            </Box>
          </Box>
        </Box>
      </Dialog>
    </Container>
  );
};

export default Settings;
